import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { IventisAggregation } from "@iventis/domain-model/model/iventisAggregation";
import { IventisAggregationType } from "@iventis/domain-model/model/iventisAggregationType";
import { SystemDataFieldName } from "@iventis/domain-model/model/systemDataFieldName";
import { Node } from "@iventis/domain-model/model/node";
import qs from "qs";
import { IventisFilterModel } from "@iventis/data-table";
import {
    ColumnDataType,
    IventisColumnApi,
    IventisServerSideDataSource,
    IventisServerSideGetRowsParams,
    ListItemCellEditorProps,
    RowNetworkStatus,
} from "@iventis/data-table/types/data-table.types";
import { getKeyIn, canEdit, isValidUuid } from "@iventis/utilities";
import { FilterFormat, FilterType } from "@iventis/types/api.types";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { Content } from "@iventis/translations";
import { createBreadcrumb, findNode } from "@iventis/tree-browser";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { getMapObjectLevelName } from "../../src/sidebar-library/sidebar-node.helpers";
import { FilterModel, MapObjectRowNodeData, SpatialDataTableContext } from "../data-table/sidebar-data-table.types";
import { getRemoteObjects } from "../state/map.slice.functions";
import { rootStore } from "../state/root.store";
import { convertMapObjectToNode } from "./library-api-functions";

export const dataTablePageSize = 1000; // Keep consistent with backend route

export interface SpatialGridParams extends IventisServerSideGetRowsParams<MapObjectRowNodeData, SpatialDataTableContext> {
    filterModel: FilterModel;
}

export const getDataSource = (
    layerPermissions: { [layerId: string]: Authorisation },
    systemDataFieldMapping: Record<SystemDataFieldName, string>,
    mapTree: Node,
    siteMaps: Sitemap[],
    siteMapConfigs: MapSitemapConfig[],
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onError?: (error: Error) => void
): IventisServerSideDataSource => {
    if (layerPermissions === undefined) {
        throw new Error(
            "Layer permissions were not provided. If there are no layers, this should be an empty object. This implies that the layer permissions were not loaded in time for the data table to present."
        );
    }
    const datasource: IventisServerSideDataSource = {
        async getRows(params: SpatialGridParams) {
            try {
                const tree = params.context?.treeBrowserContext?.currentState?.tree ?? mapTree;
                const page = (params.request.startRow ?? 0) / dataTablePageSize + 1;
                if (!Number.isInteger(page) || page < 0) {
                    throw Error(`Start row must be devisible by ${dataTablePageSize}`);
                }

                const filterModel: FilterModel =
                    params.parentNode.data == null
                        ? params.request.filterModel
                        : applyGroupToFilter(
                              params.request.groupKeys,
                              params.request.rowGroupCols.map(({ field }) => field),
                              params.request.filterModel,
                              params.columnApi
                          );

                const groupByIndex = params.request.groupKeys.length;
                const groupBy = params.request.rowGroupCols[groupByIndex];
                const aggregations: IventisAggregation[] =
                    groupBy == null
                        ? []
                        : groupBy.field === "layerId"
                        ? [{ aggregationType: IventisAggregationType.GroupBy, aggregateField: "Layer" }]
                        : [{ aggregationType: IventisAggregationType.GroupBy, aggregateField: "DataField", aggregateFieldId: groupBy.field }];

                const requestConfig = {
                    params: {
                        pageNumber: page,
                        mapId: params.context.mapId,
                        filter: JSON.stringify(dataTableFilterToIventisFilter(filterModel)),
                        sorts: sort(params),
                        aggregations: JSON.stringify(aggregations),
                    },
                    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
                };

                const res = await getRemoteObjects(requestConfig);

                const localToRemoteMapObjectIdsMap = rootStore.getState().mapReducer.mapModule.localToRemoteMapObjectIdsMap.value;

                const groupedCols = params.request.rowGroupCols;
                const groupValues = params.request.groupKeys;
                let maxIndex = 0;
                // If the start row is greater than 0, it means we are fetching a new page of an existing filter/group model
                // and thus, we must start indexing from the last row in the current list
                if (params.request.groupKeys?.length === 0 && params.request.startRow > 0) {
                    params.api.forEachNode((node) => {
                        if (node.level === 0) {
                            maxIndex = Math.max(node.data?.index ?? 0, maxIndex);
                        }
                    });
                }
                let rowData: MapObjectRowNodeData[] = [];
                const layerGroupBreadcrumbMap = new Map<string, string>();
                if (groupedCols.length === groupValues.length) {
                    const nodesToAdd: Node[] = [];
                    res.data.results.forEach((mapObject) => {
                        const node = convertMapObjectToNode(mapObject, systemDataFieldMapping[SystemDataFieldName.MapObjectName], layerPermissions);
                        // Ensure that the parent node exists in the tree
                        const layerNode = findNode(tree?.childNodes ?? [], node.parentId);

                        if (layerNode != null) {
                            const layerGroupBreadcrumb = getGroupBreadcrumb(layerGroupBreadcrumbMap, tree, layerNode);
                            nodesToAdd.push(node);
                            const permission = layerPermissions[mapObject.layerId];
                            maxIndex += 1;
                            rowData.push({
                                ...mapObject,
                                level: getMapObjectLevelName(siteMaps, mapObject.level, mapObject.sitemapId, siteMapConfigs),
                                name: node.name,
                                count: null,
                                index: maxIndex,
                                // If the Layer is remote but local on the map, store the local id
                                id: (getKeyIn(localToRemoteMapObjectIdsMap, mapObject.id) ?? mapObject.id) as string,
                                canEdit: canEdit(permission),
                                networkStatus: RowNetworkStatus.ExistsOnServer,
                                layerGroupBreadcrumb,
                            });
                        } else {
                            // eslint-disable-next-line no-console
                            console.warn(`Parent node ${node.parentId} for node ${node.id} was not found in the tree. Skipping row.`);
                        }
                    });
                    if (nodesToAdd.length > 0) {
                        params.context?.treeBrowserContext?.addNodesLocally(nodesToAdd);
                    }
                } else {
                    // If we aren't grouping by layer, we know we are grouping by list item attribute, so we must fetch the list items first
                    const allListItems =
                        (groupBy.field === "layerId"
                            ? []
                            : ((params.columnApi.getColumn(groupBy.field).getColDef().cellEditorParams as ListItemCellEditorProps).componentParams
                                  ?.listItems as DataFieldListItem[])) ?? [];

                    rowData = res.data.results.map((obj) => {
                        const { layerId, dataFieldValues, count } = obj;
                        const layerNode = findNode(tree?.childNodes ?? [], layerId);
                        const layerName = layerNode?.name ?? "Unknown layer";

                        let id = layerId;
                        let groupDisplayValue = layerName;
                        let noneGroup = false;
                        let layerGroupBreadcrumb = "";

                        // Sets the row to a none row (i.e. a row which contains objects who do not have a value for the group by field)
                        const setNoneGroup = () => {
                            id = `${groupBy.field}-none-group`;
                            noneGroup = true;
                            groupDisplayValue = params.context.translate(Content.table.none_group);
                        };
                        // If the field is a uuid, then we know if's a data field
                        if (isValidUuid(groupBy.field)) {
                            const value = dataFieldValues[groupBy.field];
                            if (typeof value === "object" && value !== null) {
                                // if the value is an object, we must be grouping by list item (which requires some validation)
                                if ("listItemAggregations" in value) {
                                    const aggs = Object.keys(value.listItemAggregations);
                                    switch (aggs.length) {
                                        case 0: {
                                            setNoneGroup();
                                            break;
                                        }
                                        case 1: {
                                            [id] = Object.keys(value.listItemAggregations);
                                            groupDisplayValue = allListItems.find((item) => item.id === id)?.name;
                                            break;
                                        }
                                        default: {
                                            throw new Error("Cannot have multiple listItems inside a single list item group");
                                        }
                                    }
                                } else {
                                    throw new Error(
                                        "In order to locate the list item id from the object, the object the datafield id is pointing to, must include the list item id as a key inside the listItemAggregations"
                                    );
                                }
                            } else if (typeof value === "string") {
                                id = value;
                                groupDisplayValue = value;
                            } else {
                                setNoneGroup();
                            }
                        }

                        if (groupBy.field === "layerId") {
                            const groupedLayerNode = findNode(tree?.childNodes ?? [], layerId);
                            layerGroupBreadcrumb = getGroupBreadcrumb(layerGroupBreadcrumbMap, tree, groupedLayerNode);
                            if (groupedLayerNode == null) {
                                // eslint-disable-next-line no-console
                                console.warn(`Layer ${layerId} was not found in the tree when grouping by layer.`);
                            }
                        }

                        maxIndex += 1;

                        return {
                            ...obj,
                            level: getMapObjectLevelName(siteMaps, obj.level, obj.sitemapId, siteMapConfigs),
                            id,
                            layerId,
                            layerName,
                            dataFieldValues,
                            index: maxIndex,
                            canEdit: false,
                            networkStatus: RowNetworkStatus.ExistsOnServer,
                            groupDisplayValue,
                            name: groupDisplayValue,
                            noneGroup,
                            count,
                            layerGroupBreadcrumb,
                        };
                    });
                }
                let lastRow;
                if (rowData.length < dataTablePageSize) {
                    lastRow = (params.request.startRow ?? 0) + rowData.length;
                }

                // The datasource is sometimes fetched before the grid api is initialised, which throws an error incorrectly
                // So check before we run the success callback so the catch correctly identifies actual errors
                if (params?.api?.getModel() != null) {
                    params.success({ rowData, rowCount: lastRow });
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                onError?.(e);
            }
        },
    };

    return datasource;
};

/*
    Conversion from ag-grid filter to domain filter/sort
*/

export const sort = (params: SpatialGridParams) => {
    const {
        request: { sortModel },
    } = params;
    let sortString = "";

    const sortSet = new Set();

    sortModel.forEach(({ colId, sort }) => {
        switch (colId) {
            case "layerId": {
                sortSet.add(fieldNameWithSortSuffix("LayerId", sort));
                break;
            }
            default: {
                sortSet.add(fieldNameWithSortSuffix(colId, sort));
                break;
            }
        }
    });

    sortString = Array.from(sortSet).join(",");
    return sortString;
};

const fieldNameWithSortSuffix = (fieldName: string, sort: "asc" | "desc") => (sort === "desc" ? `${fieldName}Desc` : `${fieldName}Asc`);

const applyGroupToFilter = (groupKeys: string[], colIdsGrouped: string[], filterModel: FilterModel, columnApi: IventisColumnApi) =>
    // We only filter on the group columns included in the groupKeys (i.e. if we group by "Layer" and "DataField - Route", and the group keys are empty, we are returning all the layers so no filtering is applied)
    colIdsGrouped.slice(0, Math.min(groupKeys.length)).reduce(
        (currentModel, colId, index) => ({
            ...currentModel,
            [colId]: getFilterForFilterType(colId, columnApi.getColumn(colId).getColDef().filterParams?.apiFilterType, groupKeys[index]),
        }),
        filterModel
    );

const getFilterForFilterType = (colId: string, filterType: FilterType, value: string): FilterModel[string] => {
    switch (filterType) {
        case "IventisFilterString":
            return {
                filterType: ColumnDataType.TEXT,
                operator: IventisFilterOperator.Eq,
                fieldName: colId,
                apiFilterType: "IventisFilterString",
                values: value,
            };
        case "IventisFilterGuidArray":
            return {
                filterType: ColumnDataType.LIST,
                operator: IventisFilterOperator.In,
                fieldName: colId,
                apiFilterType: "IventisFilterGuidArray",
                values: [{ id: value }],
            };
        default:
            throw new Error("Filter type not supported for grouping");
    }
};

export const dataTableFilterToIventisFilter = (filterModel: FilterModel): FilterFormat[] => {
    const filters = Object.keys(filterModel).map((key) => ({
        $type: filterModel[key]?.apiFilterType,
        fieldName: getFilterFieldName(filterModel[key].fieldName),
        operator: getFilterOperator(filterModel[key].operator),
        value: getFilterValues(filterModel[key]),
    }));

    return filters || [];
};

const getFilterFieldName = (headerName: string) => {
    switch (headerName) {
        case "Layer":
            return "LayerId";
        default:
            return headerName;
    }
};

const getFilterOperator = (operator: IventisFilterOperator) => {
    switch (operator) {
        // Add each case once domain operator enum is in the domain model (currently in template)
        default:
            return operator;
    }
};

const getFilterValues = (filterModel: IventisFilterModel) => {
    const { filterType, values } = filterModel;
    switch (filterType) {
        case ColumnDataType.LIST:
            return values.map((v) => v.id);
        case ColumnDataType.NUMBER:
            return Number(values);
        default:
            return values;
    }
};

const getGroupBreadcrumb = (map: Map<string, string>, tree: Node, layerNode: Node): string => {
    if (layerNode == null || tree == null) {
        return "";
    }
    // Check map, if not found, recursively find the parent folder
    if (map.has(layerNode.parentId)) {
        return map.get(layerNode.parentId);
    }
    const breadcrumb = createBreadcrumb(layerNode, tree, [NodeType.Map], { includeSelf: false })
        .map(({ name }) => name)
        .join(" > ");
    if (breadcrumb.length > 0) {
        map.set(layerNode.parentId, breadcrumb);
    }
    return breadcrumb;
};
